import React, { useState, useEffect, useCallback } from "react";
import { aiAssistantApi } from "../../api/aiAssistant.api";
import {
    FaSave, FaSpinner, FaDatabase, FaRobot, FaListUl,
    FaCheckCircle, FaExclamationTriangle, FaSyncAlt, FaTable
} from "react-icons/fa";

function PanelControlIA() {
    const [config, setConfig] = useState({
        system_prompt: "",
        behavior_rules: [],
        enabled_sources: [],
    });
    const [datamartRules, setDatamartRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState(null);
    const [newRule, setNewRule] = useState("");

    // --- Cargar configuración de la IA ---
    const fetchConfig = useCallback(async () => {
        setLoading(true);
        try {
            const data = await aiAssistantApi.getConfig();
            setConfig(data);
        } catch (err) {
            setAlert({ type: "error", msg: `Error al cargar config: ${err.message}` });
        } finally {
            setLoading(false);
        }
    }, []);

    // --- Cargar reglas autorizadas desde Datamart ---
    const fetchDatamartRules = useCallback(async () => {
        setRulesLoading(true);
        try {
            const rules = await aiAssistantApi.getDatamartRules();
            setDatamartRules(Array.isArray(rules) ? rules : []);
        } catch (err) {
            // No bloquear al usuario si Datamart no responde; dejar lista vacía
            console.warn("No se pudieron cargar reglas de Datamart:", err.message);
            setDatamartRules([]);
        } finally {
            setRulesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConfig();
        fetchDatamartRules();
    }, [fetchConfig, fetchDatamartRules]);

    // --- Guardar configuración ---
    const handleSave = async () => {
        setSaving(true);
        setAlert(null);
        try {
            await aiAssistantApi.updateConfig(config);
            setAlert({ type: "success", msg: "Configuración guardada exitosamente." });
        } catch (err) {
            setAlert({ type: "error", msg: err.message });
        } finally {
            setSaving(false);
        }
    };

    // --- Fuentes: toggle con código de regla Datamart ---
    const toggleSource = (code) => {
        setConfig((prev) => ({
            ...prev,
            enabled_sources: prev.enabled_sources.includes(code)
                ? prev.enabled_sources.filter((s) => s !== code)
                : [...prev.enabled_sources, code],
        }));
    };

    const selectAllSources = () => {
        setConfig((prev) => ({
            ...prev,
            enabled_sources: datamartRules.map((r) => r.code),
        }));
    };

    const deselectAllSources = () => {
        setConfig((prev) => ({ ...prev, enabled_sources: [] }));
    };

    // --- Reglas de comportamiento ---
    const addRule = () => {
        if (!newRule.trim()) return;
        setConfig((prev) => ({
            ...prev,
            behavior_rules: [...prev.behavior_rules, newRule.trim()],
        }));
        setNewRule("");
    };

    const removeRule = (idx) => {
        setConfig((prev) => ({
            ...prev,
            behavior_rules: prev.behavior_rules.filter((_, i) => i !== idx),
        }));
    };

    const updateRule = (idx, value) => {
        setConfig((prev) => {
            const rules = [...prev.behavior_rules];
            rules[idx] = value;
            return { ...prev, behavior_rules: rules };
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48 text-[#888]">
                <FaSpinner className="animate-spin mr-2" /> Cargando configuración...
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 sm:gap-6">

            {/* Alert */}
            {alert && (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm border ${alert.type === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                    {alert.type === "success" ? <FaCheckCircle /> : <FaExclamationTriangle />}
                    {alert.msg}
                </div>
            )}

            {/* ── Sección 1: Fuentes de datos desde Datamart ── */}
            <div className="bg-white border border-gray-200 rounded-lg p-3.5 sm:p-5 shadow-sm">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                        <FaDatabase className="text-blue-600" />
                        <h3 className="font-semibold text-[#333] text-base m-0">Fuentes de Datos Habilitadas</h3>
                    </div>
                    <button
                        type="button"
                        onClick={fetchDatamartRules}
                        className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 transition-colors"
                        title="Recargar reglas desde Datamart"
                    >
                        <FaSyncAlt className={rulesLoading ? "animate-spin" : ""} />
                        Actualizar
                    </button>
                </div>
                <p className="text-xs text-[#888] mb-4">
                    Estas fuentes se obtienen dinámicamente de las reglas activas configuradas en el módulo <strong>Datamart</strong>.
                    Solo podrás seleccionar las que estén habilitadas en la configuración de acceso de IA.
                </p>

                {rulesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-[#888] py-3">
                        <FaSpinner className="animate-spin" /> Consultando Datamart...
                    </div>
                ) : datamartRules.length === 0 ? (
                    <div className="flex flex-col items-center py-6 text-[#aaa] text-sm gap-2">
                        <FaTable className="text-2xl" />
                        <span>No hay consultas habilitadas en Datamart.</span>
                        <span className="text-xs text-[#bbb]">Ve al módulo Datamart → Configuración IA y activa las que necesitas.</span>
                    </div>
                ) : (
                    <>
                        {/* Seleccionar / Deseleccionar todo */}
                        <div className="flex gap-3 mb-3">
                            <button
                                type="button"
                                onClick={selectAllSources}
                                className="text-xs text-blue-600 hover:underline"
                            >
                                Seleccionar todas
                            </button>
                            <span className="text-[#ddd]">|</span>
                            <button
                                type="button"
                                onClick={deselectAllSources}
                                className="text-xs text-gray-500 hover:underline"
                            >
                                Ninguna
                            </button>
                        </div>

                        <div className="flex flex-col gap-2">
                            {datamartRules.map((rule) => {
                                const enabled = config.enabled_sources.includes(rule.code);
                                return (
                                    <label
                                        key={rule.code}
                                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${enabled
                                            ? "border-blue-300 bg-blue-50"
                                            : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={enabled}
                                            onChange={() => toggleSource(rule.code)}
                                            className="mt-1 accent-blue-600"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`font-medium text-sm m-0 ${enabled ? "text-blue-700" : "text-[#444]"}`}>
                                                    {rule.name}
                                                </p>
                                                <span className="text-[10px] font-mono bg-gray-100 text-[#888] px-1.5 py-0.5 rounded">
                                                    {rule.code}
                                                </span>
                                            </div>
                                            {rule.description && (
                                                <p className="text-xs text-[#888] m-0 mt-0.5">{rule.description}</p>
                                            )}
                                            {(rule.target_db || rule.target_table) && (
                                                <p className="text-[10px] text-[#aaa] m-0 mt-1 font-mono">
                                                    {rule.target_db}{rule.target_table ? ` → ${rule.target_table}` : ""}
                                                </p>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* ── Sección 2: Prompt del sistema ── */}
            <div className="bg-white border border-gray-200 rounded-lg p-3.5 sm:p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <FaRobot className="text-blue-600" />
                    <h3 className="font-semibold text-[#333] text-base m-0">Prompt del Sistema</h3>
                </div>
                <p className="text-sm text-[#666] mb-3">
                    Define la personalidad y el rol base del asistente. Este texto se incluye al inicio de cada conversación.
                </p>
                <textarea
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm text-[#333] resize-y min-h-[120px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                    value={config.system_prompt}
                    onChange={(e) => setConfig((prev) => ({ ...prev, system_prompt: e.target.value }))}
                    placeholder="Describe el rol y comportamiento base del asistente..."
                />
            </div>

            {/* ── Sección 3: Reglas de comportamiento ── */}
            <div className="bg-white border border-gray-200 rounded-lg p-3.5 sm:p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                    <FaListUl className="text-blue-600" />
                    <h3 className="font-semibold text-[#333] text-base m-0">Reglas de Comportamiento</h3>
                </div>
                <p className="text-sm text-[#666] mb-4">
                    Restricciones y directivas adicionales que el asistente debe seguir estrictamente.
                </p>

                <div className="flex flex-col gap-2 mb-4">
                    {config.behavior_rules.map((rule, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                            <span className="text-blue-400 text-xs font-bold w-5 shrink-0">{idx + 1}.</span>
                            <input
                                type="text"
                                className="flex-1 min-w-0 border border-gray-200 rounded-md px-3 py-1.5 text-sm text-[#333] focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                                value={rule}
                                onChange={(e) => updateRule(idx, e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={() => removeRule(idx)}
                                className="!w-7 !h-7 !flex-none !grow-0 !shrink-0 !bg-transparent !p-0 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 text-sm rounded-md transition-colors"
                                title="Eliminar regla"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        type="text"
                        className="flex-1 border border-gray-200 rounded-md px-3 py-1.5 text-sm text-[#333] focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                        value={newRule}
                        onChange={(e) => setNewRule(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addRule()}
                        placeholder="Nueva regla de comportamiento..."
                    />
                    <button
                        onClick={addRule}
                        className="px-4 py-2 sm:py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-md text-sm text-[#555] transition-colors shrink-0 text-center"
                    >
                        + Agregar
                    </button>
                </div>
            </div>

            {/* ── Guardar ── */}
            <div className="flex flex-col sm:flex-row justify-end">
                <button
                    className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed py-2.5 px-6"
                    onClick={handleSave}
                    disabled={saving || config.enabled_sources.length === 0}
                >
                    {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    {saving ? "Guardando..." : "Guardar Configuración"}
                </button>
            </div>
        </div>
    );
}

export default PanelControlIA;
